
import { useCallback, useEffect, useState } from 'react';
import { ApiFactory } from '@/api/api-factory';
import { AdminAuthState } from '@/api/types/admin-api-types';

// Initial state for authentication
const initialAuthState: AdminAuthState = {
  isAuthenticated: false,
  isLoading: true,
  error: null
};

/**
 * Custom hook to manage admin authentication state
 */
export const useAdminAuth = () => {
  const [authState, setAuthState] = useState<AdminAuthState>(initialAuthState);
  const adminClient = ApiFactory.getAdminClient();
  
  // Load auth state on mount
  useEffect(() => {
    const loadAuthState = async () => {
      try {
        const state = await adminClient.getAuthState();
        setAuthState(state);
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: error as Error
        });
      }
    };
    
    loadAuthState();
  }, [adminClient]);
  
  // Login function
  const login = useCallback(async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const { data, error } = await adminClient.login(email, password);
      
      if (error) throw error;
      
      if (data.user) {
        const isAdminUser = await adminClient.isAdmin(data.user.id);
        
        if (isAdminUser) {
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            userId: data.user.id,
            email: data.user.email
          });
          return { success: true };
        } else {
          // User exists but is not an admin
          await adminClient.logout();
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            error: new Error("You don't have admin privileges")
          });
          return { success: false, error: "You don't have admin privileges" };
        }
      }
      
      return { success: false, error: "Unknown error" };
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        error: error as Error
      });
      return { success: false, error: (error as Error).message };
    }
  }, [adminClient]);
  
  // Logout function
  const logout = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await adminClient.logout();
      setAuthState({
        isAuthenticated: false,
        isLoading: false
      });
      return { success: true };
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as Error
      }));
      return { success: false, error: (error as Error).message };
    }
  }, [adminClient]);
  
  return {
    ...authState,
    login,
    logout
  };
};
